<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card class="fill-height">
          <v-card-title class="pa-2">
            <v-toolbar
              :color="!$vuetify.theme.dark ? 'grey lighten-3' : ''"
              extension-height="60"
              flat
            >
              <template #default>
                <v-btn
                  v-if="userCanAdd"
                  :fab="$vuetify.breakpoint.mobile"
                  :small="$vuetify.breakpoint.mobile"
                  class="px-2 mr-1"
                  color="primary"
                  @click="createItem()"
                >
                  <v-icon :class="{'mr-1': !$vuetify.breakpoint.mobile}">
                    mdi-plus
                  </v-icon>
                  <template v-if="!$vuetify.breakpoint.mobile">
                    {{ $t(`${ $options.name }.Table.Button.New`).toLocaleUpperCase($i18n.locale) }}
                  </template>
                </v-btn>

                <v-btn
                  v-if="userCan('Permissions', PermissionEnum.ACCESS)"
                  :fab="$vuetify.breakpoint.mobile"
                  :small="$vuetify.breakpoint.mobile"
                  :to="{name: 'Permissions'}"
                  class="px-2 mr-1"
                  color="blue-grey"
                  dark
                >
                  <v-icon
                    :class="{'mr-1': !$vuetify.breakpoint.mobile}"
                    v-text="$t('Route.Permissions.Icon')"
                  />
                  <template v-if="!$vuetify.breakpoint.mobile">
                    {{ $t('Route.Permissions.Title').toLocaleUpperCase($i18n.locale) }}
                  </template>
                </v-btn>

                <v-btn
                  v-if="userCan('Roles', PermissionEnum.ACCESS)"
                  :fab="$vuetify.breakpoint.mobile"
                  :small="$vuetify.breakpoint.mobile"
                  :to="{name: 'Roles'}"
                  class="px-2 mr-1"
                  color="blue-grey"
                  dark
                >
                  <v-icon
                    :class="{'mr-1': !$vuetify.breakpoint.mobile}"
                    v-text="$t('Route.Roles.Icon')"
                  />
                  <template v-if="!$vuetify.breakpoint.mobile">
                    {{ $t('Route.Roles.Title').toLocaleUpperCase($i18n.locale) }}
                  </template>
                </v-btn>

                <div class="flex-grow-1" />

                <data-table-search
                  v-if="dataTable.search.enabled"
                  v-model="dataTable.search.terms"
                />

                <data-table-filter
                  v-model="dataTable"
                  :filters="filterData"
                />
              </template>
            </v-toolbar>
          </v-card-title>

          <v-data-table
            :footer-props="{itemsPerPageOptions: [5,10,15,20,25]}"
            :headers="dataTable.headers"
            :height="dataTable.resize.bodyHeight"
            :items="dataTable.data"
            :loading="dataTable.loading"
            :options.sync="dataTable.options"
            :search="dataTable.search.terms"
            :server-items-length="dataTable.total"
            fixed-header
            item-key="Id"
          >
            <template #[`item.RoleName`]="{item}">
              <v-chip
                :color="item.RoleColor"
                class="d-inline-block text-center"
                dark
                label
                small
                style="min-width: 100px"
              >
                {{ item.RoleName }}
              </v-chip>
            </template>

            <template #[`item.SingleLogin`]="{item}">
              <v-switch
                v-model="item.SingleLogin"
                :disabled="!userCanManage"
                :false-value="true"
                :readonly="!userCanManage"
                :true-value="false"
                class="ma-0 pt-0 d-inline-flex"
                color="success"
                hide-details
                inset
                @change="item.Update()"
              />
            </template>

            <template #[`item.Active`]="{item}">
              <v-switch
                v-model="item.Active"
                :disabled="!userCanManage || user.Id === item.Id"
                :readonly="!userCanManage || user.Id === item.Id"
                class="ma-0 pt-0 d-inline-flex"
                color="success"
                hide-details
                inset
                @change="item.Update()"
              />
            </template>

            <template
              v-if="userCanDelete || userCanManage || userCanAccess"
              #[`item.Actions`]="{item}"
            >
              <v-speed-dial
                v-model="item.fab"
                direction="left"
                right
              >
                <template #activator>
                  <v-btn
                    v-model="item.fab"
                    color="blue darken-2"
                    dark
                    depressed
                    fab
                    x-small
                  >
                    <v-icon v-if="item.fab">
                      mdi-close
                    </v-icon>
                    <v-icon v-else>
                      mdi-menu
                    </v-icon>
                  </v-btn>
                </template>

                <v-tooltip
                  v-if="userCanDelete"
                  :open-delay="650"
                  bottom
                  color="red"
                >
                  <template #activator="{on}">
                    <v-btn
                      v-if="userCanDelete"
                      :disabled="item.loadingDelete"
                      :loading="item.loadingDelete"
                      class="ml-1 white--text"
                      color="red"
                      depressed
                      fab
                      x-small
                      v-on="on"
                      @click.native="item.fab = false"
                      @click.stop="deleteItem(item)"
                    >
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </template>
                  <span v-text="$t('Common.Button.Delete')" />
                </v-tooltip>

                <v-tooltip
                  v-if="userCanAccess"
                  :open-delay="650"
                  bottom
                  color="info"
                >
                  <template #activator="{on}">
                    <v-btn
                      v-if="userCanAccess"
                      :disabled="item.loadingView"
                      :loading="item.loadingView"
                      class="ml-1 white--text"
                      color="info"
                      depressed
                      fab
                      x-small
                      v-on="on"
                      @click.native="item.fab = false"
                      @click.stop="viewItem(item)"
                    >
                      <v-icon>mdi-eye</v-icon>
                    </v-btn>
                  </template>
                  <span v-text="$t('Common.Button.View')" />
                </v-tooltip>

                <v-tooltip
                  v-if="userCanManage"
                  :open-delay="650"
                  bottom
                  color="grey"
                >
                  <template #activator="{on}">
                    <v-btn
                      v-if="userCanManage"
                      :disabled="item.loadingEdit"
                      :loading="item.loadingEdit"
                      class="ml-1 white--text"
                      color="grey"
                      depressed
                      fab
                      x-small
                      v-on="on"
                      @click.native="item.fab = false"
                      @click.stop="editItem(item)"
                    >
                      <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span v-text="$t('Common.Button.Edit')" />
                </v-tooltip>
              </v-speed-dial>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <user-edit-dialog
      v-if="userCanAccess || userCanAdd || userCanManage"
      :companies="filterData.CompanyId.Items"
      :item="selectedItem"
      :item-view-mode="itemViewMode"
      :roles="filterData.RoleId.Items"
      :visible.sync="editDialogVisible"
      @form:save:success="getData"
    />
  </v-container>
</template>

<script>
import API              from '@/api/Api'
import RoleModel        from '@/api/models/role/RoleModel'
import CompanyModel     from '@/api/models/company/CompanyModel'
import DataTable        from '@/mixins/table/dataTable'
import DataTableActions from '@/mixins/table/dataTableActions'
import UserModel        from '@/api/models/user/UserModel'
import { collect }      from '@/lib/utils/array'
import { themeUtils }   from '@/lib/utils'
import PermissionEnum   from '@/api/enums/PermissionEnum'
import i18nRouteMeta    from '@/mixins/i18n/i18nRouteMeta'

const DataTableFilter = () => themeUtils.importThemeComponent('components/common/dataTable/DataTableFilter')
const DataTableSearch = () => themeUtils.importThemeComponent('components/common/dataTable/DataTableSearch')
const UserEditDialog = () => themeUtils.importThemeComponent('views/system/user/UserEditDialog')

export default {
  name      : 'Users',
  components: {
    DataTableSearch,
    DataTableFilter,
    UserEditDialog
  },
  directives: {},
  mixins    : [i18nRouteMeta, DataTable, DataTableActions],
  props     : {},
  enums     : {},
  dataModel : UserModel,
  data () {
    return {
      dataTable: {
        customFilter: {},
        filter      : {
          Active     : null,
          SingleLogin: null,
          RoleId     : null,
          CompanyId  : null
        },
        options: {
          sortBy  : ['Username'],
          sortDesc: [false]
        },
        headers: [
          {
            text    : this.$t(`${ this.$options.name }.Table.Headers.Id`),
            value   : 'Id',
            align   : 'left',
            sortable: true
          },
          {
            text    : this.$t(`${ this.$options.name }.Table.Headers.FirstName`),
            value   : 'FirstName',
            align   : 'left',
            sortable: true
          },
          {
            text    : this.$t(`${ this.$options.name }.Table.Headers.LastName`),
            value   : 'LastName',
            align   : 'left',
            sortable: true
          },
          {
            text    : this.$t(`${ this.$options.name }.Table.Headers.Username`),
            value   : 'Username',
            align   : 'left',
            sortable: true
          },
          {
            text    : this.$t(`${ this.$options.name }.Table.Headers.Email`),
            value   : 'Email',
            align   : 'left',
            sortable: true
          },
          {
            text    : this.$t(`${ this.$options.name }.Table.Headers.Role`),
            value   : 'RoleName',
            align   : 'center',
            class   : 'pl-8',
            sortable: false
          },
          {
            text    : this.$t(`${ this.$options.name }.Table.Headers.SingleLogin`),
            value   : 'SingleLogin',
            align   : 'center',
            class   : 'pl-8',
            sortable: true
          },
          {
            text    : this.$t(`${ this.$options.name }.Table.Headers.Active`),
            value   : 'Active',
            align   : 'center',
            class   : 'pl-8',
            sortable: true
          },
          {
            text    : this.$t(`${ this.$options.name }.Table.Headers.CreatedAt`),
            value   : 'CreatedAt',
            align   : 'center',
            class   : 'pl-8',
            sortable: true
          },
          {
            text    : this.$t(`${ this.$options.name }.Table.Headers.Actions`),
            value   : 'Actions',
            align   : 'right',
            sortable: false,
            width   : 40
          }
        ]
      }
    }
  },
  computed: {},
  watch   : {},
  beforeCreate () {},
  created () {},
  beforeMount () {},
  mounted () {},
  beforeUpdate () {},
  updated () {},
  beforeDestroy () {},
  destroyed () {},
  methods : {
    getFilterData () {
      this.getRoles()
      this.getCompanies()
    },

    getRoles () {
      if (!this.userCan('Role', PermissionEnum.API)) return

      API.Resource.Role.DropDownList()
        .then(response => {
          if (API.isResponseSuccess(response)) {
            this.filterData.RoleId.Items = collect(response.data?.data?.items || [], RoleModel)
          }
        })
        .catch(e => { })
        .finally(() => { })
    },

    getCompanies () {
      if (!this.userCan('Company', PermissionEnum.API)) return

      API.Resource.Company.DropDownList()
        .then(response => {
          if (API.isResponseSuccess(response)) {
            this.filterData.CompanyId.Items = collect(response.data?.data?.items || [], CompanyModel)
          }
        })
        .catch(e => { })
        .finally(() => { })
    }
  }
}
</script>

<style scoped>

</style>
